import { useRef, type FC, ReactNode } from "react";
import { motion, AnimatePresence, useTransform, useScroll } from "framer-motion";
import { HeroCopy } from "@tennr/frontend/components/HeroCopy";
import { getEasingFunction } from "@tennr/frontend/utils";
import "./HeroSection.css";

import { useMobile } from "@tennr/frontend/hooks/useMobile";
import { Heading } from "@sanity/ui";

interface HeroSectionProps {
  noAnimate?: boolean;
  cmsContent?: any; // Generic CMS content from heroSection schema
  encodeDataAttribute?: (path: (string | number)[]) => string | undefined;
  className: string;  
  children?: ReactNode;
}

export const HeroSection: FC<HeroSectionProps> = ({ noAnimate, cmsContent, encodeDataAttribute, className, children }) => {
  const isMobile = useMobile();
  const heroRef = useRef<HTMLElement>(null!);

  // Use Framer Motion's useScroll instead of scroll-rig
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  // Call useTransform hooks at the top level
  const headingOpacity = useTransform(scrollYProgress, [0, 0.5], [0.9, 1.0]);
  const headingColor = useTransform(scrollYProgress, [0.5, 0.5], ["#DEDFD1", "rgba(238, 223, 209, 1)"]);
  const bodyOpacity = useTransform(scrollYProgress, [0, 1], [0.9, 1.0]);
  const bodyColor = useTransform(scrollYProgress, [1, 1], ["#DEDFD1", "rgba(238, 223, 209, 1)"]);

  const headingMotion = {
    style: {
      opacity: headingOpacity,
      // color: headingColor,
    },
    initial: noAnimate ? {} : { opacity: 0, y: 0, scale: 1, translate: "0 -5px" },
    animate: noAnimate ? {} : { opacity: 1, y: 0, scale: 1, translate: "0 0px" },
    exit: { opacity: 0, y: 0, scale: 1, translate: "0 0" },
    transition: {
      duration: 2,
      delay: 0.6,
      ease: getEasingFunction("easeInOutCubic"),
    },
  };

  const bodyMotion = {
    style: {
      opacity: bodyOpacity,
      // color: bodyColor,
    },
    initial: noAnimate ? {} : { opacity: 0, y: 0, scale: 1, translate: "0 0px" },
    animate: noAnimate ? {} : { opacity: 1, y: 0, scale: 1, translate: "0 0px" },
    exit: { opacity: 0, y: 0, scale: 1, translate: "0 0" },
    transition: {
      duration: 0.6,
      ease: getEasingFunction("easeInOutCubic"),
    },
  };

  // Check if we should disable animations based on CMS setting
  const disableAnimation = cmsContent?.animation === "none" || noAnimate;

  return (
    <AnimatePresence mode={"wait"}>
      <motion.section
        className={`hero-section ${(undefined !==  className) ? className : '' }`}
        ref={heroRef}        
        data-sanity={encodeDataAttribute?.(["sections", 0])}
        initial={{ opacity: 1, y: 0, translate: "0 0px" }}
        animate={{ opacity: 1, y: 0, translate: "0 0px" }}
        exit={{ opacity: 0, y: 0 }}
        transition={{
          duration: 1.6,
          delay: -0.2,
          ease: getEasingFunction("easeInOutCubic"),
        }}
      >
        <HeroCopy
          headingMotion={headingMotion}
          bodyMotion={bodyMotion}
          disableMotion={disableAnimation}
          // Pass CMS content if available, otherwise HeroCopy uses its defaults
          title={cmsContent?.heading?.title}
          heading={cmsContent?.heading?.heading}
          bodyCopy={cmsContent?.heading?.body}
          ctaText={cmsContent?.cta?.link?.text}
          ctaLink={cmsContent?.cta?.link?.url}          
        >{ children }</HeroCopy>                  
      </motion.section>
    </AnimatePresence>
  );
};
